// @ts-nocheck
import { makeAutoObservable } from 'mobx';
import RootStore from '../RootStore';

declare const navigator: Navigator;
declare const window: Window;

interface MediaDeviceInfo {
  deviceId: string;
  kind: string;
  label: string;
  groupId: string;
}

interface MediaStream {
  getTracks: () => MediaStreamTrack[];
}

interface MediaStreamTrack {
  stop: () => void;
}

interface NavigatorMediaDevices {
  enumerateDevices: () => Promise<MediaDeviceInfo[]>;
  getUserMedia: (constraints: MediaConstraints) => Promise<MediaStream>;
}

interface Navigator {
  mediaDevices: NavigatorMediaDevices;
}

interface Canvas {
  width: number;
  height: number;
}

interface MediaConstraints {
  video: {
    mandatory?: {
      sourceId?: string | null;
    };
    facingMode?: string;
  };
  audio?: boolean;
}

interface Webcam extends HTMLVideoElement {
  setAttribute: (key: string, value: string) => void;
  removeAttribute: (key: string) => void;
  addEventListener: (
    key: string,
    value: (ev: any) => void,
    bool: boolean
  ) => void;
  srcObject: any;
}

interface MediaStreamError extends Error {
  name: string;
  message: string;
  constraintName?: string;
}

interface BarcodeDetector {
  detect: (video: HTMLVideoElement) => Promise<{ rawValue: string }[]>;
}

class BarcodeCameraStore {
  active = false;
  webcam: Webcam | null = null;
  canvas: Canvas | null = null;
  ctx = null;
  decoder: Worker | null = null;
  streaming = false;

  frontCamera = false;
  cameraEnabled = false;
  qrResult = '';

  iOS = false;
  isMediaStreamAPISupported = false;
  barcodeApi = false;

  debugMode = false;
  rootStore: RootStore;
  options;

  cameraError = {};

  constructor(rootStore: RootStore, options: any) {
    // BarcodeCameraStore constructor called
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
    this.options = options;
    this.init();
  }

  init = () => {
    if (this.debugMode) console.log('init called');
    this.iOS = ['iPad', 'iPhone', 'iPod'].indexOf(navigator.platform) >= 0;
    this.isMediaStreamAPISupported =
      navigator &&
      navigator.mediaDevices &&
      'enumerateDevices' in navigator.mediaDevices;
    this.barcodeApi = 'BarcodeDetector' in window;

    // Device and API support detection complete
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  setCameraError = (err: any) => {
    this.cameraError = err;
  };

  setCanvasProperties = () => {
    if (this.debugMode) console.log('setCanvasProperties called');
    if (!this.canvas) return;
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  };

  switchCamera = () => {
    if (this.debugMode) console.log('switchCamera called');
    if (this.debugMode)
      console.log('switchCamera this.cameraEnabled', this.cameraEnabled);
    if (this.debugMode)
      console.log('switchCamera this.frontCamera', this.frontCamera);
    const cameraWasEnabled = this.cameraEnabled;
    if (cameraWasEnabled) {
      console.log('switchCamera cameraWasEnabled calling endSession');
      this.setCameraEnabled(false);
    }
    this.frontCamera = !this.frontCamera;
    if (cameraWasEnabled) {
      console.log('switchCamera cameraWasEnabled calling enableCamera');
      setTimeout(() => {
        this.setCameraEnabled(true);
      }, 1000);
    }
  };

  startCapture = (constraints: MediaConstraints) => {
    if (this.debugMode) console.log('startCapture called');
    if (this.debugMode) console.log('startCapture constraints', constraints);

    if (this.webcam) {
      this.webcam.setAttribute('playsinline', 'true');
      this.webcam.setAttribute('controls', 'true');
      setTimeout(() => {
        this.webcam?.removeAttribute('controls');
      }, 10);
    }
  };

  getWorkerURL = (url: string) => {
    const content = `importScripts( "${url}" );`;
    return URL.createObjectURL(
      new Blob([content], { type: 'text/javascript' })
    );
  };

  setPlayer = () => {
    if (this.debugMode) console.log('setPlayer called');
    const player = document.getElementById(
      'intrasync-barcode-player'
    ) as Webcam;
    this.webcam = player;

    if (this.isMediaStreamAPISupported) {
      if (this.debugMode)
        console.log('setPlayer isMediaStreamAPISupported true');
      // Resize webcam according to input
      if (this.webcam) {
        this.webcam.addEventListener(
          'play',
          (ev) => {
            if (!this.streaming) {
              this.setCanvasProperties();
              this.streaming = true;
            }
          },
          false
        );
      }
    } else {
      this.setCanvasProperties();
    }

    if (!this.barcodeApi) {
      console.log('no barcode api');
      const workerUrl = this.getWorkerURL('decoder.js');
      this.decoder = new Worker(workerUrl);
    }
  };

  setPlayerStream = (stream: MediaStream) => {
    if (this.debugMode) console.log('setPlayerStream called');
    this.webcam!.srcObject = stream;

    if (this.isMediaStreamAPISupported) {
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices: MediaDeviceInfo[]) => {
          const device = devices.filter((device: MediaDeviceInfo) => {
            if (device.kind === 'videoinput') {
              return device;
            }
            return false;
          });

          let constraints: MediaConstraints;
          if (device.length > 1) {
            if (this.debugMode) console.log('device', device);
            constraints = {
              video: {
                mandatory: {
                  sourceId: device[device.length - 1].deviceId
                    ? device[device.length - 1].deviceId
                    : null,
                },
              },
              audio: false,
            };

            if (this.iOS) {
              constraints.video.facingMode = 'environment';
            }
            this.startCapture(constraints);
          } else if (device.length) {
            constraints = {
              video: {
                mandatory: {
                  sourceId: device[0].deviceId ? device[0].deviceId : null,
                },
              },
              audio: false,
            };

            if (this.iOS) {
              constraints.video.facingMode = 'environment';
            }

            if (!constraints.video.mandatory!.sourceId && !this.iOS) {
              this.startCapture({ video: true });
            } else {
              this.startCapture(constraints);
            }
          } else {
            this.startCapture({ video: true });
          }
        })
        .catch((err: MediaStreamError) => {
          console.error('Error occurred : ', err);
          this.setCameraError(err);
        });
    }
  };

  setCanvas = () => {
    if (this.debugMode) console.log('setCanvas called');
    const canvas = document.getElementById('intrasync-barcode-canvas');
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { willReadFrequently: true });
  };

  setCameraEnabled = (value) => {
    if (this.debugMode) console.log('setCameraEnabled called');
    if (this.debugMode) console.log('setCameraEnabled value', value);
    this.cameraEnabled = value;

    if (this.cameraEnabled) {
      if (this.debugMode)
        console.log('setCameraEnabled turned on, called enableCamera');
      this.enableCamera();
    } else {
      if (this.debugMode)
        console.log('setCameraEnabled turned off, calling endSession');
      this.endSession();
    }
  };

  enableCamera = () => {
    if (this.debugMode) console.log('enableCamera called');

    this.setPlayer();
    this.setCanvas();
    if (this.debugMode) console.log('webcam', this.webcam);
    if (this.debugMode) console.log('canvas', this.canvas);

    navigator.mediaDevices
      .getUserMedia(this.constraints)
      .then((stream: MediaStream) => {
        this.setPlayerStream(stream);
        if (this.debugMode) console.log('webcam', this.webcam);
        if (this.debugMode)
          console.log('webcam srcObject', this.webcam?.srcObject);
        if (!this.barcodeApi) {
          if (this.debugMode) console.log('no barcode api');
          try {
            setTimeout(() => {
              // setCameraOverlay();
              if (this.isMediaStreamAPISupported) {
                if (this.debugMode) console.log('calling scan');
                try {
                  this.scan((result: string) => {
                    if (result) {
                      console.log('result', result);
                      this.setQrResults(result);

                      this.setCameraEnabled(false);
                    }
                  });
                } catch (err) {
                  console.log('scan err', err);
                  this.setCameraError(err);
                }
              }
            }, 1000);
          } catch (err) {
            console.warn('Barcode Camera err', err);
            this.setCameraError(err);
          }
        } else {
          const barcodeDetector = new (window as any).BarcodeDetector({
            formats: ['qr_code'],
          }) as BarcodeDetector;
          this.intervalDispose = setInterval(async () => {
            const detectedQr = await barcodeDetector.detect(
              this.webcam as HTMLVideoElement
            );
            if (detectedQr.length <= 0) return;

            if (this.debugMode) console.log('detectedQr', detectedQr);
            const result = detectedQr[0].rawValue;
            this.setQrResults(result);
          }, 1000);
        }
      })
      .catch((err: MediaStreamError) => {
        console.warn('getUserMedia Error', err);
        this.setCameraError(err);
        // if (err?.message === 'Permission denied') {
        //   this.rootStore.notificationStore.addNotification({
        //     message: `Camera Permission Denied`,
        //     options: { variant: 'Error' }
        //   });
        // }
        this.setCameraEnabled(false);
      });
  };

  scan = () => {
    if (this.debugMode) console.log('scan called');
    this.active = true;
    if (this.debugMode) console.log('this.active', this.active);
    // this.setCanvas();
    if (this.debugMode) console.log('this.canvas', this.canvas);
    if (this.debugMode) console.log('this.ctx', this.ctx);
    if (this.debugMode) console.log('this.decoder', this.decoder);

    // Start QR-decoder

    if (!this.decoder) {
      return console.warn('Decoder was not setup yet');
    }
    this.decoder.onmessage = (event) => this.onDecoderMessage(event);
    if (this.debugMode) console.log('this.decoder', this.decoder);

    this.newDecoderFrame();
  };

  newDecoderFrame = () => {
    if (this.debugMode) console.log('newDecoderFrame called');
    if (this.debugMode) console.log('this.active', this.active);
    if (!this.active) return;
    try {
      if (this.debugMode) console.log('this.ctx', this.ctx);
      this.ctx.drawImage(
        this.webcam,
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      if (this.debugMode) console.log('this.ctx drawImage', this.ctx);
      const imgData = this.ctx.getImageData(
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      if (this.debugMode) console.log('this.ctx getImageData', this.ctx);

      if (imgData.data) {
        this.decoder.postMessage(imgData);
      }
    } catch (e) {
      // Try-Catch to circumvent Firefox Bug #879717
      if (e.name === 'NS_ERROR_NOT_AVAILABLE') {
        setTimeout(this.newDecoderFrame, 0);
      } else {
        this.setCameraError(err);
      }
    }
  };

  clearCanvas = () => {
    if (this.debugMode) console.log('clearCanvas called');
    if (this.debugMode) console.log('clearCanvas ctx', this.ctx);
    if (this.debugMode) console.log('clearCanvas canvas', this.canvas);
    if (this.ctx) {
      this.ctx.fillStyle = 'rgba(0,0,0,0)';
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  };

  onDecoderMessage = (event) => {
    if (this.debugMode) console.log('onDecoderMessage called');
    if (this.debugMode) console.log('onDecoderMessage event', event);
    if (event.data.length > 0) {
      const newQrResult = event.data[0][2];
      if (this.debugMode) console.log('newQrResult', newQrResult);
      this.active = false;
      this.setNewQrResult(newQrResult);
      setTimeout(this.endSession, 100);
    }
    setTimeout(this.newDecoderFrame, 0);
  };

  setNewQrResult = (value) => {
    if (this.debugMode) console.log('setNewQrResult called');
    if (this.debugMode) console.log('setNewQrResult value', value);
    this.qrResult = value;
  };

  endSession = () => {
    if (this.debugMode) console.log('endSession called');
    if (this.decoder) {
      if (this.debugMode) console.log('decoder defined, terminating');
      this.decoder.terminate();
    }
    this.clearCanvas();

    if (this.webcam?.srcObject) {
      this.webcam.srcObject.getTracks().forEach((track) => {
        if (this.debugMode) console.log('calling track stop', track);
        track.stop();
        if (this.debugMode) console.log('after track stop', track);
      });
    }

    this.active = false;
    this.webcam = null;
    this.canvas = null;
    this.ctx = null;
    this.decoder = null;
    this.streaming = false;

    this.cameraEnabled = false;

    return 'Session Ended';
  };

  get constraints() {
    return {
      video: {
        facingMode: this.frontCamera ? 'user' : 'environment',
      },
    };
  }
}

export default BarcodeCameraStore;
